import React, { useEffect, useState } from "react";
import { Container, Card, Button, Spinner, Alert } from "reactstrap";
import { MenuLayout } from "../../themes/SwitchTheme";
import API from "../../api/Api";
import { CouponTemplateDto, getCouponTypeColor as getStandardCouponTypeColor } from "../../store/models/CouponTemplateDto";
import { BonusPointsDto } from "../../store/models/BonusPointsDto";
import { useDispatch } from "react-redux";
import { thunkCreateSuccessNotification, thunkCreateErrorNotification } from "../../store/actions/NotificationActions";
import "./CouponTemplatesPage.scss";

const CouponTemplatesPage: React.FC = () => {
  const dispatch = useDispatch();
  const [templates, setTemplates] = useState<CouponTemplateDto[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<CouponTemplateDto[]>([]);
  const [bonusPoints, setBonusPoints] = useState<BonusPointsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acquiringCoupon, setAcquiringCoupon] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>("ALL");
  const [sortBy, setSortBy] = useState<string>("NAME");

  useEffect(() => {
    loadData();
  }, []);

  const applyFiltersAndSort = React.useCallback(() => {
    let filtered = [...templates];
    
    // Apply filter
    if (filterType !== "ALL") {
      filtered = filtered.filter(template => template.valueType === filterType);
    }
    
    // Apply sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "NAME":
          return cleanCouponName(a.displayedCouponName || a.name).localeCompare(cleanCouponName(b.displayedCouponName || b.name));
        case "VALUE":
          const aValue = a.discountPercentage || a.discountAmount || 0;
          const bValue = b.discountPercentage || b.discountAmount || 0;
          return bValue - aValue;
        case "EXPIRY":
          if (!a.validTo && !b.validTo) return 0;
          if (!a.validTo) return 1;
          if (!b.validTo) return -1;
          return new Date(a.validTo).getTime() - new Date(b.validTo).getTime();
        case "POINTS":
          const aPoints = a.requiredPoints || 0;
          const bPoints = b.requiredPoints || 0;
          return aPoints - bPoints;
        default:
          return 0;
      }
    });
    
    setFilteredTemplates(filtered);
  }, [templates, filterType, sortBy]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [templates, filterType, sortBy, applyFiltersAndSort]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log("Loading coupon templates...");
      
      // Load templates first (essential) - using size 100 to get more coupons
      const templatesData = await API.getCouponTemplates({ size: 100 });
      console.log("Templates data received:", templatesData);
      setTemplates(templatesData.data);
      
      // Try to load bonus points (optional)
      try {
        const pointsData = await API.getBonusPointsInfo();
        console.log("Points data received:", pointsData);
        setBonusPoints(pointsData);
      } catch (pointsError) {
        console.warn("Failed to load bonus points, using fallback:", pointsError);
        // Temporary fallback with your known points for testing
        setBonusPoints({
          totalBonusPoints: 0,
          availableBonusPoints: 0
        });
      }
    } catch (error) {
      console.error("Error loading coupon templates:", error);
      setError("Fehler beim Laden der Coupons");
    } finally {
      setLoading(false);
    }
  };

  const handleAcquireCoupon = async (template: CouponTemplateDto) => {
    try {
      setAcquiringCoupon(template.couponTemplateNumber);
      await API.issueCouponToMe(template.couponTemplateNumber);
      
      // Clean up any generic code text from the display name
      const couponName = cleanCouponName(template.displayedCouponName || template.name);
      
      dispatch(thunkCreateSuccessNotification(`🎉 "${couponName}" coupon acquired successfully!`));
    } catch (error: any) {
      console.error("Error acquiring coupon:", error);
      dispatch(thunkCreateErrorNotification("Failed to acquire coupon", error));
    } finally {
      setAcquiringCoupon(null);
    }
  };

  const canAcquireCoupon = (template: CouponTemplateDto): boolean => {
    if (template.activationType === "FREE") return true;
    if (!bonusPoints || !template.requiredPoints) return false;
    return bonusPoints.availableBonusPoints >= template.requiredPoints;
  };

  const getPointsNeeded = (template: CouponTemplateDto): number => {
    if (template.activationType === "FREE" || !template.requiredPoints || !bonusPoints) return 0;
    return Math.max(0, template.requiredPoints - bonusPoints.availableBonusPoints);
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString('de-DE'); // German formatting with dots as thousands separators
  };

  const getCouponTypeColor = (valueType: string): string => {
    // Map the centralized color system to CSS class names for this page
    switch (valueType) {
      case "PERCENTAGE":
        return "percentage-type"; // Will use centralized red colors
      case "FIXED_AMOUNT":
        return "fixed-amount-type"; // Will use centralized orange colors
      case "FREE_SHIPPING":
        return "free-shipping-type"; // Will use centralized green colors
      case "GIFT":
        return "gift-type"; // Will use centralized purple colors
      default:
        return "default-type";
    }
  };

  const getValueChip = (template: CouponTemplateDto): string => {
    // Always show the main value as a chip
    switch (template.valueType) {
      case "PERCENTAGE":
        return template.discountPercentage ? `${template.discountPercentage}% RABATT` : "Prozentrabatt";
      case "FIXED_AMOUNT":
        return template.discountAmount ? `€${template.discountAmount} RABATT` : "Fester Rabatt";
      case "FREE_SHIPPING":
        return "VERSANDKOSTENFREI";
      case "GIFT":
        return "GESCHENK GRATIS";
      default:
        return "SONDERANGEBOT";
    }
  };

  const getChipStyle = (valueType: string) => {
    const baseStyle = {
      color: 'white',
      padding: '0.6rem 1.2rem',
      borderRadius: '25px',
      fontWeight: '800',
      fontSize: '0.9rem',
      whiteSpace: 'nowrap' as const,
      border: '2px solid rgba(255, 255, 255, 0.3)',
      textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
      transition: 'all 0.3s ease',
      letterSpacing: '0.5px'
    };

    // Use centralized color system
    const colors = getStandardCouponTypeColor(valueType);
    
    return {
      ...baseStyle,
      background: colors.gradient,
      boxShadow: `0 4px 16px ${colors.primary}40, 0 2px 8px rgba(0, 0, 0, 0.1)`,
      border: `2px solid ${colors.primary}30`
    };
  };

  const cleanCouponName = (name: string): string => {
    // Remove generic code prefixes and clean up the display name
    return name
      .replace(/^GENERIC CODE:\s*/i, "")
      .replace(/^CODE:\s*/i, "")
      .trim();
  };



  if (loading) {
    return (
      <MenuLayout>
        <Container className="coupon-templates-page">
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3">Lade verfügbare Coupons...</p>
          </div>
        </Container>
      </MenuLayout>
    );
  }

  if (error && templates.length === 0) {
    return (
      <MenuLayout>
        <Container className="coupon-templates-page">
          <Alert color="danger">{error}</Alert>
        </Container>
      </MenuLayout>
    );
  }

  return (
    <MenuLayout>
      <Container className="coupon-templates-page">
        <div className="page-header">
          <div className="header-content">
            <div className="title-section">
              <h1 className="page-title">
                <i className="bi bi-ticket-perforated me-3"></i>
                Coupon-Shop
              </h1>
              <p className="page-subtitle">Entdecke tolle Angebote und exklusive Vorteile</p>
              <div className="stats-row">
                <div className="stat-item">
                  <i className="bi bi-collection"></i>
                  <span>{templates.length} Coupons verfügbar</span>
                </div>
                {bonusPoints && (
                  <div className="stat-item points-balance">
                    <i className="bi bi-coin"></i>
                    <span>{formatNumber(bonusPoints.availableBonusPoints)} Punkte</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="controls-section">
            <div className="filter-controls">
              <div className="select-wrapper">
                <i className="bi bi-funnel select-icon"></i>
                <select 
                  className="filter-select compact" 
                  value={filterType} 
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="ALL">Alle Arten ({templates.length})</option>
                  <option value="PERCENTAGE">Prozentrabatt ({templates.filter(t => t.valueType === 'PERCENTAGE').length})</option>
                  <option value="FIXED_AMOUNT">Fester Betrag ({templates.filter(t => t.valueType === 'FIXED_AMOUNT').length})</option>
                  <option value="FREE_SHIPPING">Versandkostenfrei ({templates.filter(t => t.valueType === 'FREE_SHIPPING').length})</option>
                  <option value="GIFT">Geschenk ({templates.filter(t => t.valueType === 'GIFT').length})</option>
                </select>
              </div>
              
              <div className="select-wrapper">
                <i className="bi bi-sort-down select-icon"></i>
                <select 
                  className="filter-select compact" 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="NAME">Name A-Z</option>
                  <option value="VALUE">Bester Wert</option>
                  <option value="EXPIRY">Läuft bald ab</option>
                  <option value="POINTS">Wenigste Punkte</option>
                </select>
              </div>
            </div>
            
            <div className="view-summary">
              <span className="count">{filteredTemplates.length}</span> of <span className="total">{templates.length}</span>
            </div>
          </div>
        </div>

        {filteredTemplates.length === 0 ? (
          <div className="no-coupons">
            <i className="bi bi-search"></i>
            <h3>{templates.length === 0 ? 'Keine Coupons verfügbar' : 'Keine Coupons entsprechen deinen Filtern'}</h3>
            <p>{templates.length === 0 ? 'Schaue später für spannende Angebote vorbei!' : 'Versuche deine Filter- oder Sortieroptionen anzupassen'}</p>
            {templates.length > 0 && (
              <Button color="primary" onClick={() => { setFilterType("ALL"); setSortBy("NAME"); }}>
                Filter zurücksetzen
              </Button>
            )}
          </div>
        ) : (
          <div className="coupons-grid">
            {filteredTemplates.map((template) => (
              <div key={template.couponTemplateNumber} className="coupon-card-wrapper">
                <Card className={`coupon-card ${getCouponTypeColor(template.valueType)}`}>
                  {/* Image with overlays */}
                  <div className={`coupon-image ${!template.couponImageUrl ? 'no-image' : ''}`}>
                    {template.couponImageUrl ? (
                      <img src={template.couponImageUrl} alt={cleanCouponName(template.displayedCouponName || template.name)} />
                    ) : (
                      <div className="image-placeholder">
                        <i className="bi bi-ticket-perforated"></i>
                        <span>Coupon</span>
                      </div>
                    )}
                    
                    {/* Header overlay - Name (left) and Value (right) */}
                    <div className="coupon-header">
                      <h5 className="coupon-name">{cleanCouponName(template.displayedCouponName || template.name)}</h5>
                      <div className="coupon-value" style={getChipStyle(template.valueType)}>
                        {getValueChip(template)}
                      </div>
                    </div>
                    
                    {/* Footer overlay - Validity info */}
                    <div className="coupon-footer">
                      {template.validTo && (
                        <div className="detail-item">
                          <i className="bi bi-calendar-event"></i>
                          <span>Gültig bis: {new Date(template.validTo).toLocaleDateString()}</span>
                        </div>
                      )}
                      
                      {template.validityPeriodDays > 0 && (
                        <div className="detail-item">
                          <i className="bi bi-clock"></i>
                          <span>Läuft {template.validityPeriodDays} Tage nach Ausgabe ab</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="coupon-content">
                    <div className="coupon-actions">
                      {canAcquireCoupon(template) ? (
                        <Button
                          color="primary"
                          block
                          className="coupon-btn acquire-btn"
                          onClick={() => handleAcquireCoupon(template)}
                          disabled={acquiringCoupon === template.couponTemplateNumber}
                        >
                          {acquiringCoupon === template.couponTemplateNumber ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Coupon wird geholt...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-plus-circle me-2"></i>
                              Coupon holen
                            </>
                          )}
                        </Button>
                      ) : (
                        <div className="points-needed">
                          <Button disabled block className="coupon-btn insufficient-points-btn">
                            <i className="bi bi-exclamation-circle me-2"></i>
                            Benötige {formatNumber(getPointsNeeded(template))} weitere Punkte
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        )}
      </Container>
    </MenuLayout>
  );
};

export default CouponTemplatesPage;